import React from "react";
import CountUp from "react-countup";
import SVGRenderer from "../SVGRenderer";
import LazyImage from "components/elements/LazyImage/LazyImage";
import SmartLink from "components/SmartLink";
import Button from "components/elements/Button/Button";
import { isColorDark } from "helpers/contrastCalculator";

import styles from "./Card.module.scss";

/**
 * Card with accent border on one side
 * 
 * @param {Object} props - Component properties
 */
const CardAccent = React.forwardRef(({ 
	inView,
	title, 
	svg, 
	lead, 
	desc, 
	cta_text, 
	cta_link, 
	image, 
	image_alt, 
	bg_image, 
	bg_image_alt, 
	bg_color, 
	accent_color = 'var(--bs-primary)',
	className = "", 
	theme, 
	count_up,
	rating,
	...otherProps
}, ref) => {
	// Extract variable name if it's a CSS variable
	const accentColorVar = accent_color && accent_color.includes('var(') 
		? accent_color.match(/var\((.*?)[,)]/)[1].trim()
		: '--bs-primary';
	
	// Check if accent color is dark
	const isDarkAccent = isColorDark(accentColorVar);
	
	// Define accent text color CSS variable
	const accentTextColor = `var(${accentColorVar}-contrast)`;
	
	return (
		<div
			ref={ref}
			{...(theme ? { "data-bs-theme": theme } : {})}
			style={{
				...(bg_color && { backgroundColor: bg_color }),
				borderLeft: `5px solid ${accent_color}`
			}}
			className={`${styles.CardAccent} card h-100 rounded-4 shadow-sm border-top-0 border-end-0 border-bottom-0 position-relative overflow-hidden ${className} ${bg_image && "bg-transparent"}`}>
			{image && (
				<div>
					<LazyImage
						src={image}
						alt={image_alt || title}
						className="card-img-top"
						fill_mode="cover"
						disableAbsolute={true}
					/>
				</div>
			)}
			<div className="card-body p-4 d-flex flex-column">
				{svg && (
					<div className="mb-3 d-flex align-items-center">
						<div 
							className="accent-icon rounded-circle p-3 me-3" 
							style={{ 
								backgroundColor: accent_color,
								color: accentTextColor, 
								width: '60px', 
								height: '60px'
							}}>
							<SVGRenderer 
								svgString={svg}
								svgProps={{ 
									style: { 
										fill: isDarkAccent ? '#fff' : '#000' 
									} 
								}}
							/>
						</div>
						{title && <h3 className="card-title title fs-4 mb-0">{title}</h3>}
					</div>
				)}
				{!svg && title && <h3 className="card-title title fs-4 mb-0">{title}</h3>}
				{inView && count_up && (
					<CountUp
						className="count-up fs-2 fw-bold mb-3"
						end={count_up}
						duration={5}
						separator="."
						style={{ color: accent_color }}
					/>
				)}
				{lead && <p className="lead mt-2">{lead}</p>}

				{desc && (
					<p
						className="card-text desc"
						dangerouslySetInnerHTML={{ __html: desc }}></p>
				)}
				<div className="mt-auto">
					{cta_text && cta_link && (
						<SmartLink to={cta_link}>
							<Button
								size={"md"}
								variant={"link"}
								style={{ color: accent_color }}
								className="p-0"
								letterCase="uppercase">
								{cta_text} <i className="bi bi-arrow-right ms-1"></i>
							</Button>
						</SmartLink>
						)}
				</div>
			</div>
			{bg_image && (
				<img
					src={bg_image}
					alt={bg_image_alt || ""}
					className="position-absolute w-100 h-100"
					style={{
						objectFit: "cover",
						filter: "blur(15px)",
						opacity: 0.05,
						zIndex: -1,
						top: 0,
						left: 0,
					}}
					aria-hidden="true"
				/>
			)}
		</div>
	);
});

export default CardAccent;
